import { createContext, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantService } from '../../../shared/services/tenant/tenantService';

export enum TenantStatus {
  active = 'active',
  inactive = 'inactive',
  suspended = 'suspended',
  blocked = 'blocked',
}
export interface Tenant {
  id: string;
  name: string;
  type: {
    id: string,
    name: string,
  },
  fantasyName: string;
  corporateName: string;
  documentNumber: string;
  stateRegistration: string;
  municipalRegistration: string;
  zipCode: string;
  street: string;
  streetNumber: string;
  complement: string;
  phoneCode: string;
  phoneNumber: string;
  site: string;
  email: string;
  district: string;
  segmentFlag: number;
  status: TenantStatus;
  stateId: string;
  cityId: string;
  description: string;
  createdAt?: string;
  updatedAt?: string;
}

interface TenantContextData {
  // Listagem
  tenants: Tenant[];
  isLoading: boolean;
  loading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (tenant: Tenant) => void;
  handleCloseError: () => void;
  
  // Modal de Delete
  isDeleteModalOpen: boolean;
  selectedTenant: Tenant | null;
  openDeleteModal: (tenant: Tenant) => void;
  closeDeleteModal: () => void;
  handleDeleteTenant: () => void;
  isDeleting: boolean;
  
  // Navegação
  navigateToEdit: (id: string) => void;

  // Form
  tenantData: Tenant | null;
  isDataLoading: boolean;
  createTenant: (data: Omit<Tenant, 'id'>) => Promise<void>;
  updateTenant: (id: string, data: Partial<Tenant>) => Promise<void>;
  isSubmitting: boolean;
  getTenantById: (id: string) => Tenant;
  setError: (error: string | null) => void;
}

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantContext = createContext<TenantContextData>({} as TenantContextData);

export function TenantProvider({ children }: TenantProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Query para listar tenants
  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      try {
        const response = await tenantService.list();
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar empresas';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar empresas');
        }
        return [];
      }
    }
  });

  // Query para buscar dados do tenant no modo de edição
  const { data: tenantData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['tenant', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await tenantService.get(id);
        return response.data;
      } catch (error: any) {
        if (typeof error === 'object' && error !== null) {
          const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar empresa';
          setError(errorMessage);
        } else {
          setError('Erro ao buscar empresa');
        }
        return null;
      }
    },
    enabled: !!id,
  });

  // Estado do modal de delete
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null);

  // Mutation para deletar tenant
  const deleteMutation = useMutation({
    mutationFn: (id: string) => tenantService.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      navigate('/tenants');
    }
  });

  // Handlers para listagem
  const handleCreate = () => {
    navigate('/tenants/new');
  };

  const handleView = (tenant: Tenant) => {
    navigate(`/tenants/${tenant.id}`);
  };

  const handleCloseError = () => {
    setError(null);
  };

  // Handlers para modal de delete
  const openDeleteModal = (tenant: Tenant) => {
    setSelectedTenant(tenant);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedTenant(null);
  };

  const handleDeleteTenant = () => {
    if (selectedTenant?.id) {
      deleteMutation.mutate(selectedTenant.id);
    }
  };

  // Handler para navegação
  const navigateToEdit = (id: string) => {
    navigate(`/tenants/${id}/edit`);
  };

  // Mutations para criar e atualizar tenants
  const createMutation = useMutation({
    mutationFn: (data: Omit<Tenant, 'id'>) => tenantService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar empresa';
        setError(errorMessage);
      } else {
        setError('Erro ao criar empresa');
      }
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Tenant> }) => 
      tenantService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      queryClient.invalidateQueries({ queryKey: ['tenant', id] });
    },
    onError: (error: any) => {
      if (typeof error === 'object' && error !== null) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar empresa';
        setError(errorMessage);
      } else {
        setError('Erro ao atualizar empresa');
      }
    }
  });

  const createTenant = async (data: Omit<Tenant, 'id'>) => {
    await createMutation.mutateAsync(data);
  };

  const updateTenant = async (id: string, data: Partial<Tenant>) => {
    await updateMutation.mutateAsync({ id, data });
  };

  const getTenantById = (id: string) => {
    const tenant = tenants.find((t:any)=> t.id === id);
    if (!tenant) {
      throw new Error('Empresa não encontrada');
    }
    return tenant;
  };

  return (
    <TenantContext.Provider
      value={{
        // Listagem
        tenants,
        isLoading,
        loading: createMutation.isPending || updateMutation.isPending,
        error,
        handleCreate,
        handleView,
        handleCloseError,
        
        // Modal de Delete
        isDeleteModalOpen,
        selectedTenant,
        openDeleteModal,
        closeDeleteModal,
        handleDeleteTenant,
        isDeleting: deleteMutation.isPending,
        
        // Navegação
        navigateToEdit,

        // Form
        tenantData,
        isDataLoading,
        createTenant,
        updateTenant,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
        getTenantById,
        setError
      }}
    >
      {children}
    </TenantContext.Provider>
  );
}